/* eslint-disable @typescript-eslint/naming-convention */
// Medical monitoring data interfaces based on the JSON structure

export type WaveData = {
    amplitude: number;
    is_noise: boolean;
    waves: Wave[];
};

export type Wave = {
    data: number[]; // Raw signal values
    peak_value: number; // Peak amplitude in mV
    valley_value: number; // Valley amplitude in mV
    peak_index: number; // Index of peak in data array
    valley_index: number; // Index of valley in data array
    noise: boolean; // Whether this wave contains noise
    signal: boolean; // Whether signal was detected
};

export type Frame = {
    type: 'frame';
    time: number; // Time in seconds from start
    frame_type: ResponseType; // Type of measurement
    value: number; // Calculated value (TOFR %, TOFC count, PTC count)
    formatted_value: string; // Display formatted value
    wave_data: WaveData;
    parameter_index: number; // Index to corresponding parameter record
    t1Tref?: number | null; // T1/Tc in percent, null when not applicable
    warning_flags?: number; // Bitmask; non-zero means the result is unreliable
};

export type ParameterRecord = {
    type: 'parameter';
    mode: string; // 'TOFR', 'TOFC', 'PTC', etc.
    stimulus_current: number; // Stimulation current in mA
    stimulus_pulse_width: number; // Pulse width in μs
    tof_interval: number; // TOF interval value
    ptc_interval?: number; // PTC interval (if applicable)
    st_interval?: number; // ST interval (if applicable)
    time: number; // Time in seconds from start
};

export type Marker = {
    type: 'marker';
    number: number; // Marker number (1-based sequence number)
    time: number; // Time in seconds from start
};

export type MarkerAnnotation = {
    marker_num: number; // Marker number
    time: number; // Time in seconds from start
    annotation: string; // User annotation text
};

export type DataEntry = Frame | ParameterRecord | Marker;

export type MeasurementMeta = {
    serial: string; // Device serial number
    date: string; // Measurement date (YYMMDD format)
    time: string; // Start time (HH:mm:ss format)
    duration: number; // Duration in seconds
    softwareVersion: string; // Device software version
    device: 'Classic' | 'NextGen';
    uploadedBy: string; // Account that uploaded measurement
    initial_settings: {
        tof_interval: number;
        ptc_interval?: number; // PTC interval (NextGen only)
        st_interval?: number; // ST interval (NextGen only)
        pulse_current?: number; // Stimulation current in mA
        pulse_width?: number; // Pulse width in μs
    };
    auto_setup: {
        resultType?: number; // Whether auto mode was used
        supramaximalCurrent?: number; // Whether supramaximal stimulation was used
        supramaximalPulseWidth?: number; // Whether supramaximal pulse width was used
        supramaximal?: boolean; // Whether supramaximal settings were used (true/false)
    };
    isAutoSetup: boolean;
    filter: number;
    ref: string;
};

export type DynamicData = {
    measurementId: string;
    annotation: string;
    isEditable: boolean;
    marker_annotations: MarkerAnnotation[];
    startPreParalytic: boolean;
    goodSignalStrength: boolean;
    monitorUntilAcceptableRecovery: boolean;
    usedPTCProperly: boolean;
};

export type MeasurementData = {
    meta: MeasurementMeta;
    data: DataEntry[];
    dynamicData: DynamicData;
};

export type ResponseType =
    | 'TOFR'
    | 'TOFC'
    | 'PTC'
    | 'PTC-TOFR'
    | 'PTC-TOFC'
    | 'ST'
    | 'PTC-ST'
    | 'CAL'
    | 'CAL_UNPROCESSED';

export type ChartDataPoint = {
    x: string | Date | number; // Time as string, Date object, or number (for relative time in seconds)
    y: number; // Value to plot
};

export type TrendGraphData = {
    tofr: ChartDataPoint[]; // TOFR percentage points
    tofc: ChartDataPoint[]; // TOFC count points
    ptc: ChartDataPoint[]; // PTC count points
    amplitude: ChartDataPoint[]; // Amplitude points
    st: ChartDataPoint[]; // ST T1/Tc percentage points (ST mode only)
};

export type ResponseGraphData = {
    waves: Wave[];
    frameType: ResponseType;
    maxAmplitude: number;
    minAmplitude: number;
};

// Color constants matching the original implementation
export const CHART_COLORS = {
    TOFR: '#F7F6F3',
    TOFC: '#F7F6F3',
    PTC: '#E65582',
    AMPLITUDE: '#CEE5F2',
    WAVEFORM: '#CEE5F2',
    PEAK_VALLEY: '#CEE5F2',
    ST: '#009900',
    NOISE: '#969696',
    TIMELINE: '#e1e1e1'
} as const;

// Response type colors
export const RESPONSE_COLORS: Record<ResponseType, string> = {
    TOFR: CHART_COLORS.TOFR,
    TOFC: CHART_COLORS.TOFC,
    PTC: CHART_COLORS.PTC,
    'PTC-TOFR': CHART_COLORS.TOFR,
    'PTC-TOFC': CHART_COLORS.TOFC,
    ST: CHART_COLORS.ST,
    'PTC-ST': CHART_COLORS.ST,
    CAL: CHART_COLORS.TOFR,
    CAL_UNPROCESSED: CHART_COLORS.TOFR
};

// Chart configuration constants
export const CHART_CONFIG = {
    MAX_TOFR_PERCENTAGE: 110,
    TREND_PTC_AREA_HEIGHT_PCT: 25, // Height of PTC section as % of chart height (0–100 scale)
    TREND_TOFC_AREA_HEIGHT_PCT: 15, // Height of TOFC section as % of chart height (0–100 scale)
    MAX_ZOOM: 12
} as const;
